import React from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Button,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
} from "@mui/material";
import {
  Dashboard,
  School,
  People,
  Settings,
  AdminPanelSettings,
  ArrowBack,
  Logout,
  Group,
  HowToReg,
  Assessment,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { baseUrl, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";

// ============================================
// Modern Dashboard Theme (MUI5)
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 6,
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

const ActivityStatsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const user = getUser();
  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  const handleMenuClick = (menu: string) => {
    history.push(`/${menu}`);
  };

  const handleLogout = () => {
    logOutUser();
    history.push('/login_admin');
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      School Activity Statistics
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students" || location.pathname === "/student_list"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools" || location.pathname === "/school_management"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management" || location.pathname === "/management_console"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>
              </NavMenu>
            </Header>

            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              <Button
                onClick={() => history.goBack()}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
                  School Activity Statistics
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Comprehensive overview of public and private school activities across all Local Government Areas.
                </Typography>
              </Box>

              <SchoolActivityStats />
            </Container>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default ActivityStatsPage;
